import { useState, useEffect } from 'react';
import { v4 as uuidv4 } from 'uuid';
import authService from '@/services/authService';

/**
 * Respondent identity for survey responses.
 * - Authenticated users use their userId
 * - Anonymous users get a unique token stored in localStorage/cookie
 */
export interface RespondentIdentity {
  /** The authenticated user's ID, or null for anonymous */
  userId: number | null;
  /** The anonymous respondent token, or null for authenticated users */
  anonymousToken: string | null;
  /** Whether the user is authenticated */
  isAuthenticated: boolean;
}

const STORAGE_KEY_PREFIX = 'dpris_respondent_';
const COOKIE_NAME_PREFIX = 'dpris_anon_';

/**
 * Generates a unique anonymous token for a survey.
 */
function generateAnonymousToken(surveyId: string): string {
  return `dpris_respondent_${surveyId}_${uuidv4()}`;
}

/**
 * Gets a cookie value by name.
 */
function getCookie(name: string): string | null {
  const match = document.cookie.match(new RegExp(`(^| )${name}=([^;]+)`));
  return match ? decodeURIComponent(match[2]) : null;
}

/**
 * Sets a cookie with a 30-day expiration.
 */
function setCookie(name: string, value: string, days: number = 30): void {
  const expires = new Date();
  expires.setTime(expires.getTime() + days * 24 * 60 * 60 * 1000);
  document.cookie = `${name}=${encodeURIComponent(value)};expires=${expires.toUTCString()};path=/;SameSite=Lax`;
}

/**
 * Hook to manage respondent identity for survey responses.
 *
 * For authenticated users:
 * - Returns userId from auth service
 * - anonymousToken is null
 *
 * For anonymous users:
 * - Returns a unique token stored in localStorage
 * - Cookie is used as backup for cross-tab consistency
 * - Token is specific to each survey
 *
 * @param surveyToken - The survey's public token or ID
 * @returns RespondentIdentity object with userId, anonymousToken, and isAuthenticated
 */
export function useRespondentIdentity(surveyToken: string): RespondentIdentity {
  const [identity, setIdentity] = useState<RespondentIdentity>(() => {
    // Check if user is authenticated
    const currentUser = authService.getCurrentUser();
    if (currentUser) {
      return {
        userId: currentUser.id,
        anonymousToken: null,
        isAuthenticated: true,
      };
    }

    // For anonymous users, try to get existing token
    const storageKey = `${STORAGE_KEY_PREFIX}${surveyToken}`;
    const cookieName = `${COOKIE_NAME_PREFIX}${surveyToken}`;

    // Try localStorage first
    let token = localStorage.getItem(storageKey);

    // Fallback to cookie
    if (!token) {
      token = getCookie(cookieName);
      if (token) {
        // Sync cookie back to localStorage
        localStorage.setItem(storageKey, token);
      }
    }

    // If no token exists, generate a new one
    if (!token) {
      token = generateAnonymousToken(surveyToken);
      localStorage.setItem(storageKey, token);
      setCookie(cookieName, token);
    } else {
      // Ensure cookie is in sync with localStorage
      setCookie(cookieName, token);
    }

    return {
      userId: null,
      anonymousToken: token,
      isAuthenticated: false,
    };
  });

  // Watch for auth state changes
  useEffect(() => {
    const checkAuth = () => {
      const currentUser = authService.getCurrentUser();
      if (currentUser && !identity.isAuthenticated) {
        setIdentity({
          userId: currentUser.id,
          anonymousToken: null,
          isAuthenticated: true,
        });
      } else if (!currentUser && identity.isAuthenticated) {
        // User logged out, switch to anonymous
        const storageKey = `${STORAGE_KEY_PREFIX}${surveyToken}`;
        const cookieName = `${COOKIE_NAME_PREFIX}${surveyToken}`;
        let token = localStorage.getItem(storageKey);

        if (!token) {
          token = generateAnonymousToken(surveyToken);
          localStorage.setItem(storageKey, token);
          setCookie(cookieName, token);
        }

        setIdentity({
          userId: null,
          anonymousToken: token,
          isAuthenticated: false,
        });
      }
    };

    // Check auth state on mount and when storage changes (logout in another tab)
    checkAuth();
    window.addEventListener('storage', checkAuth);
    return () => window.removeEventListener('storage', checkAuth);
  }, [surveyToken, identity.isAuthenticated]);

  return identity;
}

/**
 * Clears the anonymous token for a survey.
 * Useful after survey submission to allow new anonymous response.
 */
export function clearAnonymousToken(surveyToken: string): void {
  const storageKey = `${STORAGE_KEY_PREFIX}${surveyToken}`;
  const cookieName = `${COOKIE_NAME_PREFIX}${surveyToken}`;

  localStorage.removeItem(storageKey);
  // Clear cookie by setting expired date
  document.cookie = `${cookieName}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/`;
}

export default useRespondentIdentity;
